// src/screens/Admin/BannerRevenueAnalyticsScreen.js

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './BannerRevenueAnalyticsScreen.css';

import backIcon from '../../assets/backspace-icon.png';

import BannerRevenueTile from './BannerRevenueTile';

import {
  getBannerRevenueAnalytics,
} from '../../services/adminRevenueAnalyticsApi';

const PERIOD_OPTIONS = [
  {
    label: '7D',
    value: 7,
  },
  {
    label: '30D',
    value: 30,
  },
  {
    label: '90D',
    value: 90,
  },
  {
    label: 'All',
    value: 'all',
  },
];

const NETWORK_OPTIONS = [
  {
    label: 'Mainnet',
    value: 'mainnet',
  },
  {
    label: 'Testnet',
    value: 'testnet',
  },
];

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Could not load banner revenue analytics.'
  );
};

function BannerRevenueAnalyticsScreen() {
  const navigate = useNavigate();

  const [selectedDays, setSelectedDays] = useState(30);
  const [selectedNetwork, setSelectedNetwork] = useState('mainnet');

  const [analyticsData, setAnalyticsData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadAnalytics = useCallback(
    async ({ background = false } = {}) => {
      if (background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        setError('');

        const data = await getBannerRevenueAnalytics({
          days: selectedDays,
          network: selectedNetwork,
        });

        setAnalyticsData(data);
      } catch (err) {
        console.error(
          'Failed to load banner revenue analytics:',
          err
        );

        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      selectedDays,
      selectedNetwork,
    ]
  );

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleBack = () => {
    navigate('/admin/revenue-analytics');
  };

  const handlePeriodChange = (value) => {
    if (value === selectedDays) return;

    setSelectedDays(value);
  };

  const handleNetworkChange = (value) => {
    if (value === selectedNetwork) return;

    setSelectedNetwork(value);
  };

  const handleRefresh = () => {
    if (loading || refreshing) return;

    loadAnalytics({
      background: Boolean(analyticsData),
    });
  };

  return (
    <div className="revenue-analytics-screen">
      <header className="revenue-analytics-topbar">
        <button
          type="button"
          className="revenue-analytics-back-btn"
          onClick={handleBack}
          aria-label="Back to revenue analytics"
        >
          <img
            src={backIcon}
            alt=""
          />
        </button>

        <div className="revenue-analytics-topbar-title">
          <h1>Profile Banner Analytics</h1>

          <span>
            Banner discovery, conversion and revenue performance
          </span>
        </div>

        <button
          type="button"
          className="revenue-analytics-refresh-btn"
          onClick={handleRefresh}
          disabled={loading || refreshing}
        >
          {refreshing
            ? 'Refreshing...'
            : 'Refresh'}
        </button>
      </header>

      <main className="revenue-analytics-scroll">
        <section className="revenue-analytics-intro">
          <div>
            <span className="revenue-analytics-intro-eyebrow">
              Profile banner experiment
            </span>

            <h2>
              Understand how banners move users from interest to purchase
            </h2>

            <p>
              Follow users from banner discovery to preview,
              trial and purchase, then compare which banners
              and collections create the strongest demand.
            </p>
          </div>

          <div className="revenue-analytics-intro-status">
            <span>Experiment status</span>
            <strong>Live</strong>
          </div>
        </section>

        <section className="revenue-analytics-controls">
          <div className="revenue-analytics-filter-group">
            <span className="revenue-analytics-filter-label">
              Period
            </span>

            <div className="revenue-analytics-filter-options">
              {PERIOD_OPTIONS.map((option) => {
                const isActive =
                  selectedDays === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={
                      `revenue-analytics-filter-btn ${
                        isActive
                          ? 'is-active'
                          : ''
                      }`
                    }
                    onClick={() =>
                      handlePeriodChange(option.value)
                    }
                    aria-pressed={isActive}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="revenue-analytics-filter-group">
            <span className="revenue-analytics-filter-label">
              Network
            </span>

            <div className="revenue-analytics-filter-options">
              {NETWORK_OPTIONS.map((option) => {
                const isActive =
                  selectedNetwork === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={
                      `revenue-analytics-filter-btn ${
                        isActive
                          ? 'is-active'
                          : ''
                      }`
                    }
                    onClick={() =>
                      handleNetworkChange(option.value)
                    }
                    aria-pressed={isActive}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {refreshing && analyticsData && (
          <div className="revenue-analytics-refreshing-note">
            Updating analytics…
          </div>
        )}

        <BannerRevenueTile
          data={analyticsData}
          loading={loading}
          error={error}
          onRetry={() => loadAnalytics()}
        />

        {analyticsData?.generatedAt && (
          <div className="revenue-analytics-generated">
            Last generated:{' '}
            {new Date(
              analyticsData.generatedAt
            ).toLocaleString()}
          </div>
        )}
      </main>
    </div>
  );
}

export default BannerRevenueAnalyticsScreen;
